import React, { useState } from 'react';
import { Button, Input, Message, Popover } from '@fx-ui/fine-design';
import {
    copyUrl,
    dataURLtoBlob,
    isFunction,
    map,
    openInNewTab,
    openInNewTabWithATag,
    redirect
} from '~/utils';
import Intl from '@fx-ui/intl';
import { QRCodeCanvas } from 'qrcode.react';
import { IconFont } from '../../components';
import classNames from 'classnames';

interface IShareUrlInput {
    width?: number | string;
    downloadUrl: string;
    downloadName?: string;
    className?: string;
    disabled?: boolean; // input是否不可以编辑,默认为是
    hasCopy?: boolean;
    hasOpen?: boolean;
    hasDownload?: boolean;
    btnDisabled?: boolean; // 按钮不可用
    hasQRCode?: boolean;
    prefix?: string; // url前面不可编辑的部分
    value?: string; // url后面可编辑部分
    onChange?: (v: string) => void;
    onCopy?: () => void;
    customQrCodeRender?: () => React.ReactNode; // 自定义二维码展示
    onQRCodeClick?: () => void;
    customUrlRender?: () => React.ReactNode; // 自定义url节点
    extendBtns?: IExtendedBtn[];
    onBeforeClick?: () => Promise<any>; // 点击前事件
    [key: string]: any;
}

export interface IExtendedBtn {
    text: string;
    onClick: () => void;
    className?: string;
}

const warningToast = (text: string) => {
    Message.add({
        messageType: 'toast',
        message: text,
        type: 'warning'
    });
};

/**
 * 链接分享input 包含输入框,打开链接按钮,保存链接按钮和二维码分享面板
 *
 * @param props
 * @class
 */
const ShareUrlInput: React.FunctionComponent<IShareUrlInput> = (props: IShareUrlInput) => {
    const {
        width,
        downloadUrl,
        downloadName,
        className = '',
        disabled = true,
        btnDisabled = false,
        hasCopy = true,
        hasOpen = false,
        hasDownload = false,
        hasQRCode = false,
        prefix,
        value,
        onChange,
        onCopy,
        onQRCodeClick,
        copySuccessMsg,
        customUrlRender,
        extendBtns,
        onBeforeClick,
        customQrCodeRender,
        ...otherProps
    } = props;

    const [popoverVisible, setPopoverVisible] = useState(false);

    /**
     * 下载二维码图片
     */
    const DownloadQrCode = () => {
        const qrCode4download = document.getElementById('qrCode4download');
        if (qrCode4download) {
            const dataUrl = (qrCode4download as HTMLCanvasElement).toDataURL('image/png');
            if (window.navigator.msSaveBlob) {
                // 兼容IE
                const blob = dataURLtoBlob(dataUrl);
                window.navigator.msSaveBlob(blob, `${downloadName!.substring(0, 255)}.png`);
            } else {
                const a = document.createElement('a');
                a.download = `${downloadName}.png`;
                a.href = dataUrl;
                a.style.display = 'none';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }
        }
    };

    /**
     * 加载二维码弹框
     */
    const renderQrCodePane = () => {
        if (isFunction(customQrCodeRender)) {
            return customQrCodeRender();
        }
        return (
            <>
                <div className="qr-code-text">{Intl.get('msg.qrCode')}</div>
                <div>
                    <QRCodeCanvas className="qrcode-img" value={downloadUrl} size={128} />
                </div>
                <QRCodeCanvas
                    id="qrCode4download"
                    style={{ display: 'none' }}
                    value={downloadUrl}
                    size={1024}
                />
                {downloadName && (
                    <Button
                        className="download-btn"
                        size="small"
                        icon={<IconFont type="import" />}
                        type="negative"
                        onClick={DownloadQrCode}
                    >
                        {Intl.get('download')}
                    </Button>
                )}
            </>
        );
    };

    const handleVisibleChange = (popoverVisible: boolean) => {
        if (!popoverVisible) {
            setPopoverVisible(popoverVisible);
        }
    };

    /**
     * 加载复制/打开/二维码按钮
     */
    const renderInputAfterAddon = () => {
        if (!hasCopy && !hasOpen && !hasQRCode && !hasDownload) {
            return null;
        }
        const handleCopyClick = () => {
            // 异步回调时兼容老式浏览器标准
            // https://stackoverflow.com/questions/43380921/document-execcommand-copy-not-working-with-ajax/43381458#43381458
            setTimeout(() => {
                copyUrl(downloadUrl, copySuccessMsg);
                if (onCopy) {
                    onCopy();
                }
            }, 100);
        };
        return (
            <div className="btn-wrapper">
                {hasCopy && (
                    <div
                        className={classNames('btn', 'copy', { disabled: btnDisabled })}
                        onClick={() => {
                            if (btnDisabled) {
                                return;
                            }
                            if (onBeforeClick) {
                                onBeforeClick()
                                    .then(() => {
                                        handleCopyClick();
                                    })
                                    .catch(() => {
                                        warningToast(Intl.get('share.url.copy.fail'));
                                    });
                            } else {
                                handleCopyClick();
                            }
                        }}
                    >
                        {Intl.get('copy')}
                    </div>
                )}
                {hasOpen && (
                    <div
                        className={classNames('btn', 'open', { disabled: btnDisabled })}
                        onClick={() => {
                            if (btnDisabled) {
                                return;
                            }
                            if (onBeforeClick) {
                                onBeforeClick()
                                    .then(() => {
                                        openInNewTabWithATag(downloadUrl, '_blank');
                                    })
                                    .catch(() => {
                                        warningToast(Intl.get('share.url.open.fail'));
                                    });
                            } else {
                                openInNewTab(downloadUrl, '_blank');
                            }
                        }}
                    >
                        {Intl.get('open')}
                    </div>
                )}
                {hasDownload && (
                    <div
                        className={classNames('btn', 'download', { disabled: btnDisabled })}
                        onClick={() => {
                            if (btnDisabled) {
                                return;
                            }
                            if (onBeforeClick) {
                                onBeforeClick()
                                    .then(() => {
                                        redirect(downloadUrl);
                                    })
                                    .catch(() => {
                                        warningToast(Intl.get('share.url.download.fail'));
                                    });
                            } else {
                                redirect(downloadUrl);
                            }
                        }}
                    >
                        {Intl.get('download')}
                    </div>
                )}
                {hasQRCode &&
                    (btnDisabled ? (
                        <div className="btn qr-code disabled">
                            <span>
                                <IconFont type="qrcode" />
                            </span>
                        </div>
                    ) : (
                        <Popover
                            className="fx-share-url-popover"
                            content={renderQrCodePane()}
                            trigger="click"
                            placement="bottom-left"
                            visible={popoverVisible}
                            onVisibleChange={handleVisibleChange}
                            offset={[-11, 10]}
                        >
                            <div className="popover-btn">
                                <div
                                    className="btn qr-code"
                                    onClick={(event) => {
                                        event && event.stopPropagation();
                                        if (onQRCodeClick) {
                                            onQRCodeClick();
                                        }
                                        if (onBeforeClick) {
                                            onBeforeClick()
                                                .then(() => {
                                                    setPopoverVisible(true);
                                                })
                                                .catch(() => {
                                                    warningToast(Intl.get('share.url.qrCode.fail'));
                                                });
                                        } else {
                                            setPopoverVisible(true);
                                        }
                                    }}
                                >
                                    <span>
                                        <IconFont type="qrcode" />
                                    </span>
                                </div>
                            </div>
                        </Popover>
                    ))}
                {extendBtns &&
                    map(extendBtns, (btn, i) => {
                        const { text, className, onClick } = btn;
                        return (
                            <div key={i} className={classNames('btn', className)} onClick={onClick}>
                                {text}
                            </div>
                        );
                    })}
            </div>
        );
    };

    if (isFunction(customUrlRender)) {
        return (
            <div className={classNames('fx-share-input', className)} style={{ width: width }}>
                {customUrlRender()}
                {renderInputAfterAddon()}
            </div>
        );
    }

    return (
        <div className={classNames('fx-share-input', className)} style={{ width: width }}>
            {prefix && <div className="fixed-url">{prefix}</div>}
            <Input
                className={classNames('url-input', {
                    'has-before-addon': prefix
                })}
                readonly={disabled}
                value={disabled ? downloadUrl : value}
                afterAddon={renderInputAfterAddon()}
                onChange={(v) => onChange && onChange(v)}
                {...otherProps}
            />
        </div>
    );
};

export default ShareUrlInput;
